import { useCallback, useState } from 'react'
import { DEFAULT_LITELLM_BASE_URL } from '@saip/backend'

/** The connection the add-in was set up with, persisted per user/machine. */
export interface LiteLlmSettings {
  baseUrl: string
  virtualKey: string
  /** Chat model. */
  model: string
  /** Transcription model for dictation. Empty/absent → Spracheingabe is off. */
  transcribeModel?: string
  /** Optional separate model for the composer's "Hirn"/thinking mode. */
  thinkingModel?: string
}

const STORAGE_KEY = 'saip.litellm.v1'

/**
 * Base URL the connection dialog starts with: a build-time override
 * (VITE_LITELLM_BASE_URL) wins, otherwise the SafeAI Plus default proxy.
 */
export function defaultLiteLlmBaseUrl(): string {
  const fromEnv =
    typeof import.meta !== 'undefined'
      ? (import.meta as { env?: Record<string, string | undefined> }).env?.VITE_LITELLM_BASE_URL
      : undefined
  return fromEnv?.trim() || DEFAULT_LITELLM_BASE_URL
}

function isComplete(value: Partial<LiteLlmSettings> | null): value is LiteLlmSettings {
  return !!value?.baseUrl?.trim() && !!value.virtualKey?.trim() && !!value.model?.trim()
}

/**
 * Read the stored connection. Returns null when nothing was saved yet, when the
 * stored record is incomplete, or when storage is unavailable — every one of
 * those means "not configured", which sends the user to the setup screen.
 */
export function loadLiteLlmSettings(): LiteLlmSettings | null {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY)
    if (!raw) return null
    const parsed = JSON.parse(raw) as Partial<LiteLlmSettings>
    return isComplete(parsed) ? parsed : null
  } catch {
    // Corrupt JSON, or a WebView with site data blocked.
    return null
  }
}

export function saveLiteLlmSettings(settings: LiteLlmSettings): void {
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(settings))
  } catch {
    // Nothing to do — the in-memory state still drives this session.
  }
}

export function clearLiteLlmSettings(): void {
  try {
    window.localStorage.removeItem(STORAGE_KEY)
  } catch {
    // ignore
  }
}

export interface UseLiteLlmSettings {
  /** null → not configured yet; the app must show the setup screen. */
  settings: LiteLlmSettings | null
  save: (settings: LiteLlmSettings) => void
  clear: () => void
}

export function useLiteLlmSettings(): UseLiteLlmSettings {
  const [settings, setSettings] = useState<LiteLlmSettings | null>(() => loadLiteLlmSettings())

  const save = useCallback((next: LiteLlmSettings) => {
    saveLiteLlmSettings(next)
    setSettings(next)
  }, [])

  const clear = useCallback(() => {
    clearLiteLlmSettings()
    setSettings(null)
  }, [])

  return { settings, save, clear }
}
